/**
 * NutriPoints Loyalty Widget Web Component
 * Displays loyalty points balance and handles UI updates
 */

if (!customElements.get("loyalty-widget")) {
  // Helper function to decode HTML entities from Shopify translations
  function decodeHTML(html) {
    const txt = document.createElement('textarea');
    txt.innerHTML = html;
    return txt.value;
  }

  // Helper function to build locale-aware URLs
  function getLocalizedUrl(path) {
    const localeRoot = window.loyaltyConfig?.localeRoot || '';
    // If localeRoot is just '/', return the path as-is to avoid double slashes
    if (localeRoot === '/' || localeRoot === '') {
      return path;
    }
    return localeRoot + path;
  }

  class LoyaltyWidget extends HTMLElement {
    constructor() {
      super();
      this.customerId = null;
      this.balance = 0;
      this.loading = true;
      this.error = null;
      this.unsubscribers = [];
    }

    connectedCallback() {
      this.customerId = this.getAttribute("customer-id");
      this.displayMode = this.getAttribute("display-mode") || "badge";

      // Initialize tier products if not already set
      if (!window.loyaltyTierProducts) {
        window.loyaltyTierProducts = {
          tier_150: [],
          tier_300: [],
          tier_500: [],
          tier_700: []
        };
      }

      // Subscribe to cart events to refresh balance
      this.subscribeToEvents();

      // Render when strings are ready
      this.whenStringsReady().then(() => {
        this.render();
      });

      // Load balance if customer is logged in
      if (this.customerId) {
        this.loadBalance();
      } else {
        this.loading = false;
        // If cart-progress mode and not logged in, try to load tier products anyway for preview
        if (this.displayMode === "cart-progress") {
          this.loadTierProducts();
        }
        this.render();
      }
    }

    disconnectedCallback() {
      // Unsubscribe from events when element is removed
      this.unsubscribers.forEach((unsub) => unsub());
    }

    whenStringsReady() {
      // Return a promise that resolves when window.strings is available
      if (window.strings?.loyalty) {
        return Promise.resolve();
      }

      return new Promise((resolve) => {
        // If DOMContentLoaded hasn't fired yet, wait for it
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => resolve(), { once: true });
        } else {
          // DOMContentLoaded already fired, strings should be available
          // Use a microtask to give inline scripts a chance to execute
          Promise.resolve().then(() => resolve());
        }
      });
    }

    subscribeToEvents() {
      // Subscribe to cart events (if PubSub is available)
      if (typeof subscribe === "function") {
        const cartEvents = ["cart-add", "cart-update", "cart-remove"];

        cartEvents.forEach((event) => {
          const unsub = subscribe(event, () => {
            // Refresh balance after cart changes
            if (this.customerId) {
              this.loadBalance();
            }
          });
          this.unsubscribers.push(unsub);
        });
      }
    }

    async loadBalance() {
      if (!window.loyaltyAPI || !this.customerId) {
        return;
      }

      this.loading = true;
      this.error = null;
      this.render();

      try {
        // App Proxy automatically includes customer ID from Shopify session
        const data = await window.loyaltyAPI.getBalance();

        this.balance = data.balance || 0;

        // Store tier products if included in balance response
        if (data.tier_products) {
          window.loyaltyTierProducts = data.tier_products;
        }

        this.error = null;
      } catch (error) {
        console.error("Failed to load loyalty balance:", error);
        this.error = error.message;
      } finally {
        this.loading = false;
        this.render();
      }
    }

    async loadTierProducts() {
      if (!window.loyaltyAPI) {
        return;
      }

      try {
        // Use the public tier-products endpoint (no login required)
        const data = await window.loyaltyAPI.getTierProducts();
        if (data.tier_products) {
          window.loyaltyTierProducts = data.tier_products;
        }

        this.render();
      } catch (error) {
        console.error("Failed to load tier products:", error);
        // Fallback to mock products
        window.loyaltyTierProducts = window.loyaltyAPI.getMockTierProducts();
        this.render();
      }
    }

    render() {
      if (this.displayMode === "badge") {
        this.renderBadge();
      } else if (this.displayMode === "cart-notification") {
        this.renderCartNotification();
      } else if (this.displayMode === "header-button") {
        this.renderHeaderButton();
      } else if (this.displayMode === "drawer-button") {
        this.renderDrawerButton();
      } else if (this.displayMode === "mobile-star") {
        this.renderMobileStar();
      } else if (this.displayMode === "cart-progress") {
        this.renderCartProgress();
      } else if (this.displayMode === "earn-message") {
        this.renderEarnMessage();
      }
    }

    renderBadge() {
      const badgeStyle = this.getAttribute("badge-style") || "icon_text";
      const strings = window.strings?.loyalty?.badge || {};
      const ctaText =
        this.getAttribute("cta-text") || strings.cta || "Join NutriPoints!";

      if (this.loading) {
        this.innerHTML = `
          <div class="loyalty-badge loading">
            <span class="loyalty-badge__loader"></span>
          </div>
        `;
        return;
      }

      if (this.error) {
        // Silently fail - don't show error in badge
        this.innerHTML = "";
        return;
      }

      if (!this.customerId) {
        // Not logged in - show CTA
        this.innerHTML = `
          <a href="${getLocalizedUrl('/pages/nutripoints')}" class="loyalty-badge loyalty-badge--cta">
            <svg class="loyalty-badge__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor"/>
            </svg>
            <span class="loyalty-badge__text">${ctaText}</span>
          </a>
        `;
        return;
      }

      // Logged in - show balance
      const formattedBalance = window.loyaltyAPI.formatPoints(this.balance);
      const showIcon = badgeStyle === "icon_text" || badgeStyle === "icon_only";
      const showText = badgeStyle === "icon_text" || badgeStyle === "text_only";
      const label = strings.label || "NutriPoints";
      const titleText = (strings.title || "{{ points }} NutriPoints").replace(
        "{{ points }}",
        formattedBalance,
      );

      this.innerHTML = `
        <a href="${getLocalizedUrl('/pages/nutripoints')}" class="loyalty-badge loyalty-badge--balance" title="${titleText}">
          ${
            showIcon
              ? `<svg class="loyalty-badge__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor"/>
            </svg>`
              : ""
          }
          ${
            showText
              ? `<span class="loyalty-badge__text">
              <span class="loyalty-badge__points">${formattedBalance}</span>
              <span class="loyalty-badge__label">${label}</span>
            </span>`
              : ""
          }
        </a>
      `;
    }

    renderCartNotification() {
      if (!this.customerId || this.error) {
        this.innerHTML = "";
        return;
      }

      // Calculate points from current cart
      const cartTotal = this.getAttribute("cart-total") || "0";
      const points = window.loyaltyAPI.calculatePointsFromCart(
        parseInt(cartTotal, 10),
      );

      if (points === 0) {
        this.innerHTML = "";
        return;
      }

      const formattedPoints = window.loyaltyAPI.formatPoints(points);
      const strings = window.strings?.loyalty?.cart || {};
      const earnMessage = (
        strings.earnMessage ||
        "You'll earn <strong>{{ points }} NutriPoints</strong> with this order!"
      ).replace("{{ points }}", formattedPoints);
      const viewRewards = strings.viewRewards || "View rewards";

      this.innerHTML = `
        <div class="loyalty-cart-notification">
          <svg class="loyalty-cart-notification__icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor"/>
          </svg>
          <div class="loyalty-cart-notification__content">
            <p class="loyalty-cart-notification__text">
              ${earnMessage}
            </p>
            <a href="${getLocalizedUrl('/pages/nutripoints')}" class="loyalty-cart-notification__link">${viewRewards}</a>
          </div>
        </div>
      `;
    }

    renderHeaderButton() {
      const strings = window.strings?.loyalty?.header || {};
      const badgeStrings = window.strings?.loyalty?.badge || {};
      const buttonLabel = strings.buttonLabel || badgeStrings.label || "NutriPoints";
      const availableText = strings.available || "available";

      if (this.loading) {
        this.innerHTML = `
          <div class="header__cta header__loyalty-button header__loyalty-button--loading z-1 border-radius-input p-1 gap-1 pl-15 pr-2 whitespace-nowrap color-scheme-3 gradient">
            <span class="loyalty-badge__loader"></span>
          </div>
        `;
        return;
      }

      if (this.error) {
        // Silently fail - don't show button on error
        this.innerHTML = "";
        return;
      }

      if (!this.customerId) {
        // Not logged in - show CTA with star icon and text
        this.innerHTML = `
          <a href="${getLocalizedUrl('/pages/nutripoints')}" class="header__cta header__loyalty-button z-1 link un-underlined-link decoration-none flex items-center border-radius-input p-1 gap-1 pl-15 pr-2 whitespace-nowrap color-scheme-3 gradient">
            <svg class="icon icon--star" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
              <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor"/>
            </svg>
            <div>
              <p class="m-0 lh-13 t7"><strong>${buttonLabel}</strong></p>
            </div>
          </a>
        `;
        return;
      }

      // Logged in - show points balance with "available" subtitle
      const formattedBalance = window.loyaltyAPI.formatPoints(this.balance);

      this.innerHTML = `
        <a href="${getLocalizedUrl('/pages/nutripoints')}" class="header__cta header__loyalty-button header__loyalty-button--logged-in z-1 link un-underlined-link decoration-none flex items-center border-radius-input p-1 gap-1 pl-15 pr-2 whitespace-nowrap color-scheme-3 gradient">
          <svg class="icon icon--star" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor"/>
          </svg>
          <div>
            <p class="m-0 lh-13 t7"><strong>${formattedBalance} ${buttonLabel}</strong></p>
            <p class="m-0 lh-13 opacity-80 t7">${availableText}</p>
          </div>
        </a>
      `;
    }

    renderDrawerButton() {
      const strings = window.strings?.loyalty?.header || {};
      const badgeStrings = window.strings?.loyalty?.badge || {};
      const buttonLabel = strings.button_label || badgeStrings.label || "NutriPoints";
      const availableText = strings.available || "available";

      if (this.loading) {
        this.innerHTML = `
          <a href="${getLocalizedUrl('/pages/nutripoints')}" class="menu-drawer__loyalty-cta menu-drawer__loyalty-cta--loading">
            <span class="loyalty-badge__loader"></span>
          </a>
        `;
        return;
      }

      if (this.error) {
        // Silently fail - don't show button on error
        this.innerHTML = "";
        return;
      }

      if (!this.customerId) {
        // Not logged in - show CTA with star icon and text
        this.innerHTML = `
          <a href="${getLocalizedUrl('/pages/nutripoints')}" class="menu-drawer__loyalty-cta">
            <svg class="icon icon--star" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
              <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor"/>
            </svg>
            <div class="menu-drawer__loyalty-text">
              <p class="m-0"><strong>${buttonLabel}</strong></p>
            </div>
          </a>
        `;
        return;
      }

      // Logged in - show points balance with "available" subtitle
      const formattedBalance = window.loyaltyAPI.formatPoints(this.balance);

      this.innerHTML = `
        <a href="${getLocalizedUrl('/pages/nutripoints')}" class="menu-drawer__loyalty-cta menu-drawer__loyalty-cta--logged-in">
          <svg class="icon icon--star" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor"/>
          </svg>
          <div class="menu-drawer__loyalty-text">
            <p class="m-0"><strong>${formattedBalance} ${buttonLabel}</strong></p>
            <p class="m-0 opacity-80" style="font-size: 0.875rem;">${availableText}</p>
          </div>
        </a>
      `;
    }

    renderMobileStar() {
      const strings = window.strings?.loyalty?.header || {};
      const badgeStrings = window.strings?.loyalty?.badge || {};
      const buttonLabel = strings.button_label || badgeStrings.label || "NutriPoints";

      if (this.loading) {
        this.innerHTML = `
          <a href="${getLocalizedUrl('/pages/nutripoints')}" class="header__icon header__icon--loyalty-mobile-link link focus-inset large-up-hide">
            <span class="loyalty-badge__loader"></span>
            <span class="visually-hidden">${buttonLabel}</span>
          </a>
        `;
        return;
      }

      if (this.error) {
        // Silently fail - don't show button on error
        this.innerHTML = "";
        return;
      }

      // Show star icon for both logged in and logged out
      this.innerHTML = `
        <a href="${getLocalizedUrl('/pages/nutripoints')}" class="header__icon header__icon--loyalty-mobile-link link focus-inset large-up-hide">
          <svg class="icon icon--star" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor"/>
          </svg>
          <span class="visually-hidden">${buttonLabel}</span>
        </a>
      `;
    }

    renderCartProgress() {
      const strings = window.strings?.loyalty?.cart || {};
      const knowMoreText = strings.knowMore || "Know more";
      const freeText = strings.freeBadge || "FREE";
      const cartPoints = parseInt(this.getAttribute("cart-points") || "0", 10);

      // Define tiers
      const tiers = [
        { level: 1, points: 65, key: 'tier_150' },
        { level: 2, points: 140, key: 'tier_300' },
        { level: 3, points: 250, key: 'tier_500' },
        { level: 4, points: 400, key: 'tier_700' }
      ];

      if (this.loading) {
        this.innerHTML = `
          <div class="loyalty-progress-bar-content">
            <div class="loyalty-progress-text">
              <span class="loyalty-badge__loader"></span>
            </div>
          </div>
        `;
        return;
      }

      if (this.error) {
        // Silently fail
        this.innerHTML = "";
        return;
      }

      let totalPoints = cartPoints;
      let currentBalance = 0;

      // If logged in, add current balance to cart points
      if (this.customerId && this.balance) {
        currentBalance = this.balance;
        totalPoints = cartPoints + currentBalance;
      }

      // Find next tier
      let targetTier = null;
      let currentTier = null;

      for (let i = 0; i < tiers.length; i++) {
        if (totalPoints < tiers[i].points) {
          targetTier = tiers[i];
          if (i > 0) {
            currentTier = tiers[i - 1];
          }
          break;
        }
      }

      // If no target tier found, user has reached max tier
      if (!targetTier) {
        targetTier = tiers[tiers.length - 1];
        currentTier = targetTier;
      }

      const pointsNeeded = Math.max(0, targetTier.points - totalPoints);
      const progressPercent = Math.min(100, (totalPoints / targetTier.points) * 100);

      // Choose message based on login state
      let message = "";
      if (progressPercent >= 100) {
        message = decodeHTML(strings.tierReached || "Congratulations! You've reached a new tier!");
      } else if (this.customerId) {
        // Logged in - show points needed including balance
        message = decodeHTML((strings.progressMessageWithBalance || "<strong>{{ points }} NutriPoints</strong> to next tier!")
          .replace("{{ points }}", pointsNeeded));
      } else {
        // Logged out - show points needed to specific tier
        message = decodeHTML((strings.progressMessage || "<strong>{{ points }} NutriPoints</strong> to Tier {{ tier }}!")
          .replace("{{ points }}", pointsNeeded)
          .replace("{{ tier }}", targetTier.level));
      }

      // Get tier product preview (first product from target tier)
      let tierProductHTML = '';
      const tierProducts = window.loyaltyTierProducts?.[targetTier.key];
      if (tierProducts && tierProducts.length > 0) {
        const firstProduct = tierProducts[0];
        const viewProductText = decodeHTML(strings.viewProduct || "View product");
        const freeProductMessage = decodeHTML((strings.freeProductMessage || "This product will be free in the next order if you gather <strong>{{ points }} NutriPoints</strong>")
          .replace("{{ points }}", targetTier.points));

        tierProductHTML = `
          <div class="loyalty-tier-preview">
            <div class="loyalty-tier-preview__image">
              <img src="${firstProduct.featured_image}" alt="${firstProduct.title}" loading="lazy">
              <span class="loyalty-tier-preview__badge">${freeText}</span>
            </div>
            <div class="loyalty-tier-preview__info">
              <div class="loyalty-tier-preview__header">
                <span class="loyalty-tier-preview__name">${firstProduct.title}</span>
                <a href="${firstProduct.url}" class="loyalty-tier-preview__link">${viewProductText}</a>
              </div>
              <p class="loyalty-tier-preview__message">${freeProductMessage}</p>
            </div>
          </div>
        `;
      }

      this.innerHTML = `
        <div class="loyalty-progress-bar-content">
          <div class="loyalty-progress-text">
            <span class="loyalty-progress-message">${message}</span>
            <a href="${getLocalizedUrl('/pages/nutripoints')}" class="loyalty-progress-link">${knowMoreText}</a>
          </div>
          <div class="loyalty-progress-bar-track" style="--loyalty-bar-width: ${progressPercent}%;">
            <div class="loyalty-progress-bar-fill"><span style="display:none;">.</span></div>
          </div>
          ${tierProductHTML}
        </div>
      `;
    }

    renderEarnMessage() {
      // Get cart points from attribute
      const cartPoints = parseInt(this.getAttribute("cart-points") || "0", 10);

      // Get translations
      const strings = window.strings?.loyalty?.cart || {};
      const earnMessage = strings.earnMessageFooter || "This purchase will give you {{ points }} NutriPoints";
      const knowMoreText = strings.knowMore || "Know more";

      // Decode HTML and replace placeholder
      const decodedMessage = decodeHTML(earnMessage.replace("{{ points }}", cartPoints));

      // Render earn message with "Know more" link after the text
      this.innerHTML = `
        <small class="footer-message caption-large rte loyalty-earn-message">
          ${decodedMessage}
          <a href="${getLocalizedUrl('/pages/nutripoints')}" class="loyalty-progress-link ml-1">${knowMoreText}</a>
        </small>
      `;
    }

    // Public method to refresh balance
    refresh() {
      if (this.customerId) {
        window.loyaltyAPI?.clearCache();
        this.loadBalance();
      }
    }
  }

  customElements.define("loyalty-widget", LoyaltyWidget);
}

/**
 * Loyalty Dashboard Component
 * Full dashboard for the dedicated loyalty page
 */
if (!customElements.get("loyalty-dashboard")) {
  class LoyaltyDashboard extends HTMLElement {
    constructor() {
      super();
      this.customerId = null;
      this.balance = 0;
      this.transactions = [];
      this.tiers = [];
      this.loading = true;
    }

    connectedCallback() {
      this.customerId = this.getAttribute("customer-id");

      // 1. Read the config directly from the DOM (Works instantly, no polling required!)
      const configElement = this.querySelector("[data-loyalty-config]");
      if (configElement) {
        try {
          const config = JSON.parse(configElement.textContent);
          this.bgImage = config.loggedOutBgImage;
        } catch (e) {
          console.error("Failed to parse loyalty config", e);
        }
      }

      // 2. Always show the logged-out view
      this.renderLoginPrompt();
      return;
    }

    async loadData() {
      if (!window.loyaltyAPI) {
        const strings = window.strings?.loyalty?.dashboard || {};
        this.renderError(
          strings.errorNotConfigured || "Loyalty system is not configured.",
        );
        return;
      }

      this.loading = true;
      this.render();

      try {
        // Load balance and tiers in parallel
        // App Proxy automatically includes customer ID from Shopify session
        const [balanceData, tiersData] = await Promise.all([
          window.loyaltyAPI.getBalance(),
          window.loyaltyAPI.getTiers(null),
        ]);

        this.balance = balanceData.balance || 0;
        this.transactions = balanceData.recent_transactions || [];
        this.tiers = tiersData.tiers || [];

        // Store tier products if included in balance response
        if (balanceData.tier_products) {
          window.loyaltyTierProducts = balanceData.tier_products;
        }

        this.loading = false;
        this.render();
      } catch (error) {
        console.error("Failed to load loyalty data:", error);
        this.renderError(error.message);
      }
    }

    render() {
      if (this.loading) {
        this.renderLoading();
        return;
      }

      const formattedBalance = window.loyaltyAPI.formatPoints(this.balance);
      const strings = window.strings?.loyalty || {};

      this.innerHTML = `
        <div class="loyalty-dashboard">
          <div class="loyalty-dashboard__header">
            <h1 class="loyalty-dashboard__title">${strings.dashboard?.title || "Your NutriPoints"}</h1>
            ${this.renderMetricsCards()}
          </div>

          <div class="loyalty-dashboard__section">
            <h2 class="loyalty-dashboard__section-title">${strings.sections?.redeem?.title || "Redeem Rewards"}</h2>
            <p class="loyalty-dashboard__section-description">${strings.sections?.redeem?.description || "Choose a reward tier to redeem your points. You'll receive a promo code to use at checkout."}</p>
            <div class="loyalty-dashboard__tiers">
              ${this.renderTiers()}
            </div>
          </div>

          <div class="loyalty-dashboard__section loyalty-dashboard__section--how-it-works">
            <h2 class="loyalty-dashboard__section-title">${strings.sections?.howItWorks?.title || "How It Works"}</h2>
            <div class="loyalty-dashboard__info-grid">
              <div class="loyalty-dashboard__info-card">
                <svg class="loyalty-dashboard__info-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 2C7.89543 2 7 2.89543 7 4V5H6C4.34315 5 3 6.34315 3 8V19C3 20.6569 4.34315 22 6 22H18C19.6569 22 21 20.6569 21 19V8C21 6.34315 19.6569 5 18 5H17V4C17 2.89543 16.1046 2 15 2H9ZM9 4H15V6H9V4ZM6 7H18C18.5523 7 19 7.44772 19 8V19C19 19.5523 18.5523 20 18 20H6C5.44772 20 5 19.5523 5 19V8C5 7.44772 5.44772 7 6 7Z" fill="currentColor"/>
                </svg>
                <h3 class="loyalty-dashboard__info-title">${strings.sections?.howItWorks?.shopEarnTitle || "Shop & Earn"}</h3>
                <p class="loyalty-dashboard__info-text">${strings.sections?.howItWorks?.shopEarnText || "Earn 1 NutriPoint for every 1€ you spend"}</p>
              </div>
              <div class="loyalty-dashboard__info-card">
                <svg class="loyalty-dashboard__info-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor"/>
                </svg>
                <h3 class="loyalty-dashboard__info-title">${strings.sections?.howItWorks?.collectTitle || "Collect Points"}</h3>
                <p class="loyalty-dashboard__info-text">${strings.sections?.howItWorks?.collectText || "Points are added automatically after your order"}</p>
              </div>
              <div class="loyalty-dashboard__info-card">
                <svg class="loyalty-dashboard__info-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 7H16V5C16 3.34 14.66 2 13 2H11C9.34 2 8 3.34 8 5V7H4C2.9 7 2 7.9 2 9V20C2 21.1 2.9 22 4 22H20C21.1 22 22 21.1 22 20V9C22 7.9 21.1 7 20 7ZM10 5C10 4.45 10.45 4 11 4H13C13.55 4 14 4.45 14 5V7H10V5ZM12 15.5C10.62 15.5 9.5 14.38 9.5 13C9.5 11.62 10.62 10.5 12 10.5C13.38 10.5 14.5 11.62 14.5 13C14.5 14.38 13.38 15.5 12 15.5Z" fill="currentColor"/>
                </svg>
                <h3 class="loyalty-dashboard__info-title">${strings.sections?.howItWorks?.redeemTitle || "Redeem Rewards"}</h3>
                <p class="loyalty-dashboard__info-text">${strings.sections?.howItWorks?.redeemText || "Choose rewards and get promo codes instantly"}</p>
              </div>
            </div>
          </div>

          ${this.transactions.length > 0 ? this.renderTransactions() : ""}
        </div>
      `;

      // Attach event listeners to redeem buttons
      this.attachRedeemListeners();
    }

    renderMetricsCards() {
      const formattedBalance = window.loyaltyAPI.formatPoints(this.balance);
      const strings = window.strings?.loyalty || {};

      // Calculate metrics from transactions
      const pointsUsed = this.transactions
        .filter((tx) => tx.type === "redeem")
        .reduce((sum, tx) => sum + Math.abs(tx.points), 0);

      const redeemsCount = this.transactions.filter(
        (tx) => tx.type === "redeem",
      ).length;

      const lastRedeemTx = this.transactions
        .filter((tx) => tx.type === "redeem")
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];

      const lastRedeemDate = lastRedeemTx
        ? new Date(lastRedeemTx.created_at).toLocaleDateString("en-US", {
            month: "numeric",
            day: "numeric",
          })
        : "-";

      return `
        <div class="loyalty-dashboard__metrics-grid">
          <div class="loyalty-dashboard__metric-card loyalty-dashboard__metric-card--primary">
            <svg class="loyalty-dashboard__metric-icon" width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor"/>
            </svg>
            <div class="loyalty-dashboard__metric-content">
              <p class="loyalty-dashboard__metric-label">${strings.dashboard?.balanceLabel || "Your Balance"}</p>
              <p class="loyalty-dashboard__metric-value">${formattedBalance} <span class="loyalty-dashboard__metric-unit">${strings.dashboard?.pointsUnit || "Points"}</span></p>
            </div>
          </div>

          <div class="loyalty-dashboard__metric-card">
            <div class="loyalty-dashboard__metric-content">
              <p class="loyalty-dashboard__metric-label">${strings.dashboard?.pointsUsed || "Points Used"}</p>
              <p class="loyalty-dashboard__metric-value">${pointsUsed}</p>
            </div>
          </div>

          <div class="loyalty-dashboard__metric-card">
            <div class="loyalty-dashboard__metric-content">
              <p class="loyalty-dashboard__metric-label">${strings.dashboard?.redeems || "Redeems"}</p>
              <p class="loyalty-dashboard__metric-value">${redeemsCount}</p>
            </div>
          </div>

          <div class="loyalty-dashboard__metric-card">
            <div class="loyalty-dashboard__metric-content">
              <p class="loyalty-dashboard__metric-label">${strings.dashboard?.lastRedeem || "Last Redeem"}</p>
              <p class="loyalty-dashboard__metric-value">${lastRedeemDate}</p>
            </div>
          </div>
        </div>
      `;
    }

    renderTiers() {
      const strings = window.strings?.loyalty?.tiers || {};

      if (!this.tiers || this.tiers.length === 0) {
        return `<p class="loyalty-dashboard__empty">${strings.empty || "No reward tiers available."}</p>`;
      }

      return this.tiers
        .map((tier) => {
          const canRedeem = this.balance >= tier.points_cost;
          const pointsNeeded = Math.max(0, tier.points_cost - this.balance);
          const pointsNeededText = (
            strings.points_needed || "{{ points }} more points needed"
          ).replace("{{ points }}", pointsNeeded);

          // Get products for this tier from window object (set by Liquid)
          const tierProducts = window.loyaltyTierProducts?.[tier.key] || [];
          const isVetTier = tier.key === "tier_700";

          const titleText = (strings.products_available_for || "Products available for {{ points }} NutriPoints").replace('{{ points }}', tier.points_cost);
          return `
          <div class="loyalty-tier-row ${canRedeem ? "loyalty-tier-row--available" : "loyalty-tier-row--locked"}" data-tier-key="${tier.key}">
            <div class="loyalty-tier-row__header">
              <div class="loyalty-tier-row__title-group">
                <h3 class="loyalty-tier-row__title">${titleText}</h3>
                ${
                  canRedeem
                    ? `<span class="loyalty-tier-row__status loyalty-tier-row__status--available">${strings.available || "✓ Available"}</span>`
                    : `<span class="loyalty-tier-row__status">${pointsNeededText}</span>`
                }
              </div>
            </div>
            <div class="loyalty-tier-row__content">
              ${this.renderTierProducts(tier, tierProducts, canRedeem, isVetTier)}
            </div>
          </div>
        `;
        })
        .join("");
    }

    renderTierProducts(tier, products, canRedeem, isVetTier) {
      const strings = window.strings?.loyalty?.tiers || {};

      // Special handling for vet consultation tier
      if (isVetTier) {
        // Get vet image from global variable (set by Liquid in loyalty-dashboard.liquid)
        const vetImageUrl = window.loyaltyVetImageUrl || "";

        return `
          <div class="loyalty-tier-row__vet-card">
            ${
              vetImageUrl
                ? `
              <div class="loyalty-tier-row__vet-image">
                <img src="${vetImageUrl}" alt="Veterinarian" loading="lazy" width="80" height="80" />
              </div>
            `
                : `
              <div class="loyalty-tier-row__vet-icon">👨‍⚕️</div>
            `
            }
            <div class="loyalty-tier-row__vet-content">
              <h4>${strings.vetConsultation || "Vet Nutrition Consultation"}</h4>
              <p>${strings.vetDescription || "Book a personalized nutrition consultation with our veterinary team"}</p>
            </div>
            <button
              class="loyalty-tier__redeem-btn ${canRedeem ? "" : "loyalty-tier__redeem-btn--disabled"}"
              data-tier-key="${tier.key}"
              data-is-vet="true"
              ${!canRedeem ? "disabled" : ""}
            >
              ${canRedeem ? strings.bookNow || "Book Now" : strings.locked || "Locked"}
            </button>
          </div>
        `;
      }

      // No products available
      if (!products || products.length === 0) {
        return `<p class="loyalty-tier-row__empty">${strings.noProducts || "No products available for this tier yet."}</p>`;
      }

      // Render product carousel
      const sliderId = `Slider-loyalty-${tier.key}`;

      return `
        <slider-component class="slider-mobile-gutter loyalty-tier-row__slider">
          <ul id="${sliderId}" class="grid product-grid contains-card contains-card--product contains-card--standard grid--4-col-desktop grid--2-col-tablet-down slider slider--desktop slider--tablet" role="list">
            ${products
              .map(
                (product, index) => `
              <li id="${sliderId}-Slide-${index + 1}" class="grid__item slider__slide">
                ${this.renderProductCard(product, tier, canRedeem)}
              </li>
            `,
              )
              .join("")}
          </ul>
          ${
            products.length > 4
              ? `
            <div class="slider-buttons no-js-hidden">
              <button type="button" class="slider-button slider-button--prev" name="previous" aria-label="Slide left" aria-controls="${sliderId}">
                <svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path>
                </svg>
              </button>
              <div class="slider-counter caption">
                <span class="slider-counter--current">1</span>
                <span aria-hidden="true"> / </span>
                <span class="visually-hidden">of</span>
                <span class="slider-counter--total">${products.length}</span>
              </div>
              <button type="button" class="slider-button slider-button--next" name="next" aria-label="Slide right" aria-controls="${sliderId}">
                <svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path>
                </svg>
              </button>
            </div>
          `
              : ""
          }
        </slider-component>
      `;
    }

    renderProductCard(product, tier, canRedeem) {
      const strings = window.strings?.loyalty?.tiers || {};
      const firstVariant = product.variants?.[0] || {};

      return `
        <div class="card-wrapper product-card-wrapper underline-links-hover ${!canRedeem ? "product-card-wrapper--disabled" : ""}">
          <div class="card card--standard card--media">
            <div class="card__inner color-scheme-1 gradient ratio" style="--ratio-percent: 100%;">
              <div class="card__media">
                <div class="media media--transparent media--hover-effect">
                  ${
                    product.featured_image
                      ? `
                    <img
                      srcset="${product.featured_image}"
                      src="${product.featured_image}"
                      sizes="(min-width: 990px) 25vw, (min-width: 750px) 33vw, 50vw"
                      alt="${product.title}"
                      class="motion-reduce"
                      loading="lazy"
                      width="300"
                      height="300"
                    >
                  `
                      : `
                    <div class="card__media-placeholder">
                      <svg class="placeholder-svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525.5 525.5">
                        <path d="M324.5 212.7H203c-1.6 0-2.8 1.3-2.8 2.8V308c0 1.6 1.3 2.8 2.8 2.8h121.6c1.6 0 2.8-1.3 2.8-2.8v-92.5c0-1.6-1.3-2.8-2.9-2.8zm1.1 95.3c0 .6-.5 1.1-1.1 1.1H203c-.6 0-1.1-.5-1.1-1.1v-92.5c0-.6.5-1.1 1.1-1.1h121.6c.6 0 1.1.5 1.1 1.1V308z"/>
                        <path d="M210.4 299.5H240v.1s.1 0 .2-.1h75.2v-76.2h-105v76.2zm1.8-7.2l20-20c1.6-1.6 3.8-2.5 6.1-2.5s4.5.9 6.1 2.5l1.5 1.5 16.8 16.8c-12.9 3.3-20.7 6.3-22.8 7.2h-27.7v-5.5zm101.5-10.1c-20.1 1.7-36.7 4.8-49.1 7.9l-16.9-16.9 26.3-26.3c1.6-1.6 3.8-2.5 6.1-2.5s4.5.9 6.1 2.5l27.5 27.5v7.8zm-68.9 15.5c9.7-3.5 24.9-6.9 44.4-9.5l11.4 11.4H244.8v-.1c.1 0 .1.1 0 .1l-.1-.1.1.1zm-2.5-18.8c-.9-.9-1.9-1.8-2.9-2.6l20.7-20.7c.9.9 1.9 1.8 2.8 2.7l-20.6 20.6zm-18.8-7.2l-9.9-9.9-1.5-1.5c-.4-.4-.9-.7-1.3-1.1v-54.3h95.6v54.3c-.4.4-.9.8-1.3 1.1l-9.9 9.9-26.3-26.3c-3.6-3.6-9.4-3.6-13 0l-32.4 32.4c0-.1 0-.1 0 0z"/>
                      </svg>
                    </div>
                  `
                  }
                </div>
              </div>
              <div class="card__content card__content--badge">
                <div class="card__badge bottom left">
                  <span class="badge badge--bottom-left loyalty-product-badge">${strings.free_badge || "GRÁTIS"}</span>
                </div>
              </div>
            </div>
            <div class="card__content">
              <div class="card__information">
                <h3 class="card__heading">
                  <a href="${product.url}" class="full-unstyled-link">
                    ${product.title}
                  </a>
                </h3>
                <div class="card-information">
                  <span class="caption-large light"></span>
                  <div class="price">
                    <div class="price__container">
                      <div class="price__sale">
                        <span class="visually-hidden visually-hidden--inline">${strings.salePrice || "Sale price"}</span>
                        <span class="price-item price-item--sale price-item--last">
                          €0.00
                        </span>
                        <span class="visually-hidden visually-hidden--inline">${strings.originalPrice || "Original price"}</span>
                        <s class="price-item price-item--regular">
                          ${this.formatPrice(firstVariant.price)}
                        </s>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="card__content">
              <div class="card__information">
                <div class="loyalty-tier__product-actions">
                  <button
                    class="button button--full-width loyalty-tier__redeem-btn ${canRedeem ? "" : "loyalty-tier__redeem-btn--disabled"}"
                    data-tier-key="${tier.key}"
                    data-product-id="${product.id}"
                    data-variant-id="${firstVariant.id}"
                    data-product-title="${product.title}"
                    data-product-image="${product.featured_image}"
                    data-product-description="${product.description || ""}"
                    data-product-price="${firstVariant.price}"
                    data-tier-cost="${tier.points_cost}"
                    ${!canRedeem ? "disabled" : ""}
                  >
                    ${canRedeem ? strings.tiers?.redeem || "Redeem" : strings.tiers?.locked || "Locked"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      `;
    }

    formatPrice(priceInCents) {
      const priceInEuros = priceInCents / 100;
      return (
        window.loyaltyAPI?.formatCurrency(priceInEuros) ||
        `€${priceInEuros.toFixed(2)}`
      );
    }

    formatTransactionDescription(tx) {
      const strings = window.strings?.loyalty?.transactions || {};

      // Check if description contains a code pattern (e.g., "NP-300-2452B7BE")
      const codeMatch = tx.description.match(/NP-\d+-[A-F0-9]+/i);

      if (codeMatch) {
        const code = codeMatch[0];
        // Extract "Used by user" text or use translated version
        const usedByText = strings.code_used || "Used by user";
        return `${strings.code_label || "Code"}: ${code} - ${usedByText}`;
      } else {
        // No code found, check if it's a manual points addition
        return strings.manual_points || "Manual points added";
      }
    }

    renderTransactions() {
      const strings = window.strings?.loyalty?.sections?.recentActivity || {};

      return `
        <div class="loyalty-dashboard__section">
          <h2 class="loyalty-dashboard__section-title">${strings.title || "Recent Activity"}</h2>
          <div class="loyalty-transactions">
            ${this.transactions
              .map(
                (tx) => `
              <div class="loyalty-transaction loyalty-transaction--${tx.type}">
                <div class="loyalty-transaction__icon">
                  ${
                    tx.type === "earn"
                      ? '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
                      : '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M17 7L7 17M7 17H17M7 17V7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
                  }
                </div>
                <div class="loyalty-transaction__content">
                  <p class="loyalty-transaction__description">${this.formatTransactionDescription(tx)}</p>
                  <p class="loyalty-transaction__date">${new Date(tx.created_at).toLocaleDateString()}</p>
                </div>
                <div class="loyalty-transaction__points ${tx.points > 0 ? "loyalty-transaction__points--positive" : "loyalty-transaction__points--negative"}">
                  ${tx.points > 0 ? "+" : ""}${tx.points}
                </div>
              </div>
            `,
              )
              .join("")}
          </div>
        </div>
      `;
    }

    attachRedeemListeners() {
      const buttons = this.querySelectorAll(
        ".loyalty-tier__redeem-btn:not(.loyalty-tier__redeem-btn--disabled)",
      );

      buttons.forEach((button) => {
        button.addEventListener("click", async (e) => {
          const tierKey = e.target.getAttribute("data-tier-key");
          const productId = e.target.getAttribute("data-product-id");
          const variantId = e.target.getAttribute("data-variant-id");
          const productTitle = e.target.getAttribute("data-product-title");
          const productImage = e.target.getAttribute("data-product-image");
          const productDescription = e.target.getAttribute(
            "data-product-description",
          );
          const productPrice = parseInt(
            e.target.getAttribute("data-product-price"),
          );
          const tierCost = parseInt(e.target.getAttribute("data-tier-cost"));
          const isVet = e.target.getAttribute("data-is-vet") === "true";

          // For vet consultations, use the old flow
          if (isVet) {
            await this.handleRedeem(
              tierKey,
              productId,
              variantId,
              productTitle,
              isVet,
              e.target,
            );
          } else {
            // Show confirmation modal first
            this.showConfirmationModal({
              tierKey,
              productId,
              variantId,
              title: productTitle,
              image: productImage,
              description: productDescription,
              price: productPrice,
              tierCost: tierCost,
            });
          }
        });
      });
    }

    async handleRedeem(
      tierKey,
      productId,
      variantId,
      productTitle,
      isVet,
      buttonElement,
    ) {
      if (!window.loyaltyAPI || !this.customerId) {
        return;
      }

      const strings = window.strings?.loyalty?.redeem || {};
      const originalText = buttonElement.textContent;
      buttonElement.textContent = strings.processing || "Processing...";
      buttonElement.disabled = true;

      try {
        // App Proxy automatically includes customer ID from Shopify session
        const result = await window.loyaltyAPI.redeemPoints(
          tierKey,
          productId,
          variantId,
        );

        // Show appropriate modal based on tier type
        if (isVet) {
          this.showVetConfirmationModal(result);
        } else {
          this.showRedemptionModal(result, productTitle, variantId);
        }

        // Update balance immediately
        this.balance = result.remaining_balance;
        this.updateBalanceDisplay();
        this.updateTierAvailability();
      } catch (error) {
        const errorMsg = (
          strings.error || "Failed to redeem points: {{ message }}"
        ).replace("{{ message }}", error.message);
        alert(errorMsg);
        buttonElement.textContent = originalText;
        buttonElement.disabled = false;
      }
    }

    showConfirmationModal(productData) {
      const strings = window.strings?.loyalty?.modal || {};

      const modal = document.createElement("div");
      modal.className = "loyalty-modal-overlay";
      modal.innerHTML = `
        <div class="loyalty-modal loyalty-modal--confirmation">
          <div class="loyalty-modal__accent-bar"></div>
          <button class="loyalty-modal__close" aria-label="Close">×</button>

          <div class="loyalty-modal__product-image-container">
            ${
              productData.image
                ? `
              <img src="${productData.image}" alt="${productData.title}" class="loyalty-modal__product-image">
            `
                : ""
            }
          </div>

          <h2 class="loyalty-modal__product-title">${productData.title}</h2>

          ${
            productData.description
              ? `
            <p class="loyalty-modal__description">${productData.description}</p>
          `
              : ""
          }

          <div class="loyalty-modal__price-display">
            <span class="loyalty-modal__price-original">${this.formatPrice(productData.price)}</span>
            <span class="loyalty-modal__price-free">${strings.freeBadge || "FREE"}</span>
          </div>

          <div class="loyalty-modal__points-info">
            <p>${strings.costInfo || "This will cost"} <strong>${productData.tierCost} NutriPoints</strong></p>
            <p>${strings.remainingInfo || "Remaining balance:"} <strong>${this.balance - productData.tierCost} points</strong></p>
          </div>

          <div class="loyalty-modal__button-group">
            <button class="loyalty-modal__btn-cancel">
              ${strings.cancel || "Cancel"}
            </button>
            <button class="loyalty-modal__btn-confirm"
                    data-tier-key="${productData.tierKey}"
                    data-product-id="${productData.productId}"
                    data-variant-id="${productData.variantId}"
                    data-product-title="${productData.title}">
              ${strings.confirmRedeem || "Confirm Redeem"}
            </button>
          </div>
        </div>
      `;

      document.body.appendChild(modal);

      // Event listeners
      const closeBtn = modal.querySelector(".loyalty-modal__close");
      const cancelBtn = modal.querySelector(".loyalty-modal__btn-cancel");
      const confirmBtn = modal.querySelector(".loyalty-modal__btn-confirm");
      const overlay = modal;

      const closeModalHandler = () => this.closeModal(modal);

      closeBtn.addEventListener("click", closeModalHandler);
      cancelBtn.addEventListener("click", closeModalHandler);
      overlay.addEventListener("click", (e) => {
        if (e.target === overlay) closeModalHandler();
      });

      confirmBtn.addEventListener("click", async () => {
        const tierKey = confirmBtn.getAttribute("data-tier-key");
        const productId = confirmBtn.getAttribute("data-product-id");
        const variantId = confirmBtn.getAttribute("data-variant-id");
        const productTitle = confirmBtn.getAttribute("data-product-title");

        // Close confirmation modal
        this.closeModal(modal);

        // Show processing state (we'll create a simple loading overlay)
        const loadingOverlay = document.createElement("div");
        loadingOverlay.className = "loyalty-modal-overlay";
        loadingOverlay.innerHTML = `
          <div class="loyalty-modal loyalty-modal--loading">
            <div class="loyalty-dashboard__loader"></div>
            <p>${strings.processing || "Processing redemption..."}</p>
          </div>
        `;
        document.body.appendChild(loadingOverlay);

        try {
          // Call the redemption API
          const result = await window.loyaltyAPI.redeemPoints(
            tierKey,
            productId,
            variantId,
          );

          // Remove loading overlay
          this.closeModal(loadingOverlay);

          // Update balance
          this.balance = result.remaining_balance;

          // Show success modal first (before re-rendering to avoid breaking cart drawer)
          this.showRedemptionModal(result, productTitle, variantId);

          // Reload tiers in background to update availability
          const tiersData = await window.loyaltyAPI.getTiers(null);
          this.tiers = tiersData.tiers || [];

          // Store reference to re-render later when modal closes
          this._pendingRender = true;
        } catch (error) {
          // Remove loading overlay
          this.closeModal(loadingOverlay);

          // Show error
          const errorMsg =
            error.message ||
            strings.error ||
            "Failed to redeem. Please try again.";
          alert(errorMsg);
        }
      });
    }

    showRedemptionModal(result, productTitle, variantId) {
      const strings = window.strings?.loyalty?.modal || {};
      const promoCode = result.promo_code;

      const modal = document.createElement("div");
      modal.className = "loyalty-modal-overlay";
      modal.innerHTML = `
        <div class="loyalty-modal loyalty-modal--success">
          <button class="loyalty-modal__close" aria-label="Close">×</button>
          <div class="loyalty-modal__icon">✓</div>
          <h2 class="loyalty-modal__title">${strings.successTitle || "Redemption Successful!"}</h2>
          <p class="loyalty-modal__subtitle">${strings.productRedeemed || "Product redeemed:"} <strong>${productTitle}</strong></p>

          <div class="loyalty-modal__promo-section">
            <p class="loyalty-modal__promo-label">${strings.promoCodeLabel || "Your promo code:"}</p>
            <div class="loyalty-modal__promo-code">${promoCode}</div>
            <button class="loyalty-modal__copy-btn" data-code="${promoCode}">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 4V2.5C4 1.67157 4.67157 1 5.5 1H13.5C14.3284 1 15 1.67157 15 2.5V10.5C15 11.3284 14.3284 12 13.5 12H12V13.5C12 14.3284 11.3284 15 10.5 15H2.5C1.67157 15 1 14.3284 1 13.5V5.5C1 4.67157 1.67157 4 2.5 4H4Z" stroke="currentColor" stroke-width="1.5"/>
              </svg>
              ${strings.copyCode || "Copy Code"}
            </button>
          </div>

          <div class="loyalty-modal__points-info">
            <p>${strings.newBalance || "New balance:"} <strong>${result.remaining_balance} points</strong></p>
          </div>

          <button class="loyalty-modal__cta-btn" data-variant-id="${variantId}" data-promo-code="${promoCode}">
            ${strings.addToCart || "Add to Cart & Apply Code"}
          </button>
        </div>
      `;

      document.body.appendChild(modal);

      // Event listeners
      const closeBtn = modal.querySelector(".loyalty-modal__close");
      const copyBtn = modal.querySelector(".loyalty-modal__copy-btn");
      const ctaBtn = modal.querySelector(".loyalty-modal__cta-btn");
      const overlay = modal;

      closeBtn.addEventListener("click", () => this.closeModal(modal));
      overlay.addEventListener("click", (e) => {
        if (e.target === overlay) this.closeModal(modal);
      });

      copyBtn.addEventListener("click", () => {
        navigator.clipboard.writeText(promoCode);
        copyBtn.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          ${strings.copied || "Copied!"}
        `;
        setTimeout(() => {
          copyBtn.innerHTML = `
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 4V2.5C4 1.67157 4.67157 1 5.5 1H13.5C14.3284 1 15 1.67157 15 2.5V10.5C15 11.3284 14.3284 12 13.5 12H12V13.5C12 14.3284 11.3284 15 10.5 15H2.5C1.67157 15 1 14.3284 1 13.5V5.5C1 4.67157 1.67157 4 2.5 4H4Z" stroke="currentColor" stroke-width="1.5"/>
            </svg>
            ${strings.copyCode || "Copy Code"}
          `;
        }, 2000);
      });

      ctaBtn.addEventListener("click", () => {
        this.addToCartWithDiscount(variantId, promoCode);
        this.closeModal(modal);
      });
    }

    showVetConfirmationModal(result) {
      const strings = window.strings?.loyalty?.modal || {};

      const modal = document.createElement("div");
      modal.className = "loyalty-modal-overlay";
      modal.innerHTML = `
        <div class="loyalty-modal loyalty-modal--vet">
          <button class="loyalty-modal__close" aria-label="Close">×</button>
          <div class="loyalty-modal__icon loyalty-modal__icon--vet">👨‍⚕️</div>
          <h2 class="loyalty-modal__title">${strings.vetTitle || "Consultation Booked!"}</h2>
          <p class="loyalty-modal__message">${strings.vetMessage || "Our veterinary nutrition team will contact you via email within 24-48 hours to schedule your consultation."}</p>

          <div class="loyalty-modal__points-info">
            <p>${strings.newBalance || "New balance:"} <strong>${result.remaining_balance} points</strong></p>
          </div>

          <button class="loyalty-modal__cta-btn loyalty-modal__cta-btn--secondary">
            ${strings.gotIt || "Got it!"}
          </button>
        </div>
      `;

      document.body.appendChild(modal);

      // Event listeners
      const closeBtn = modal.querySelector(".loyalty-modal__close");
      const ctaBtn = modal.querySelector(".loyalty-modal__cta-btn");
      const overlay = modal;

      closeBtn.addEventListener("click", () => this.closeModal(modal));
      overlay.addEventListener("click", (e) => {
        if (e.target === overlay) this.closeModal(modal);
      });
      ctaBtn.addEventListener("click", () => this.closeModal(modal));
    }

    closeModal(modal) {
      modal.remove();

      // If there's a pending render (after successful redemption), do it now
      if (this._pendingRender) {
        this._pendingRender = false;
        this.render();
      }
    }

    async addToCartWithDiscount(variantId, promoCode) {
      try {
        // Extract numeric ID from Shopify GID format if needed
        // GID format: "gid://shopify/ProductVariant/123456" -> "123456"
        const numericVariantId = variantId.includes('/')
          ? variantId.split('/').pop()
          : variantId;

        // Add product to cart using Shopify Ajax API
        // The /cart/add.js endpoint requires numeric variant ID, not GID format
        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            id: numericVariantId,
            quantity: 1,
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to add product to cart: ${response.status} - ${errorText}`);
        }

        // Apply discount code by redirecting to /discount/CODE which auto-redirects to cart
        window.location.href = "/discount/" + promoCode + "?redirect=/cart";
      } catch (error) {
        // Show user-friendly error with the promo code they can use manually
        const strings = window.strings?.loyalty?.errors || {};
        alert(
          strings.cartAddFailed ||
          "Unable to add product to cart automatically. Please add it manually and use discount code: " +
            promoCode
        );
      }
    }

    updateBalanceDisplay() {
      const balanceEl = this.querySelector(
        ".loyalty-dashboard__balance-points",
      );
      if (balanceEl) {
        const formattedBalance = window.loyaltyAPI.formatPoints(this.balance);
        const strings = window.strings?.loyalty || {};
        balanceEl.textContent = `${formattedBalance} ${strings.dashboard?.pointsUnit || "Points"}`;
      }
    }

    updateTierAvailability() {
      // Re-render tiers to update locked/available states
      const tiersContainer = this.querySelector(".loyalty-dashboard__tiers");
      if (tiersContainer) {
        tiersContainer.innerHTML = this.renderTiers();
        this.attachRedeemListeners();
      }
    }

    renderLoading() {
      const strings = window.strings?.loyalty?.dashboard || {};

      this.innerHTML = `
        <div class="loyalty-dashboard loyalty-dashboard--loading">
          <div class="loyalty-dashboard__loader"></div>
          <p>${strings.loading || "Loading your loyalty dashboard..."}</p>
        </div>
      `;
    }

    renderLoginPrompt() {
      const strings = window.strings?.loyalty?.login || {};
      // Always use customer_authentication path with return_to for New Customer Accounts
      const currentPath = window.location.pathname;
      const loginUrl = `/customer_authentication/login?return_to=${encodeURIComponent(currentPath)}`;

// Get background image directly from the component's HTML attribute
      const bgImage = this.getAttribute('data-bg-image');
      const mockBackground =
        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";
      const backgroundStyle = bgImage
        ? `background-image: url('${bgImage}')`
        : `background-image: ${mockBackground}`;

      this.innerHTML = `
        <div class="loyalty-dashboard loyalty-dashboard--logged-out">
          <!-- Hero Section with Background Image -->
          <div class="loyalty-logged-out-hero" style="${backgroundStyle}">
            <div class="loyalty-logged-out-hero__container">
              <div class="loyalty-dashboard__section loyalty-dashboard__section--how-it-works">
                <h2 class="loyalty-dashboard__section-title">${window.strings?.loyalty?.logged_out?.heroTitle || 'Nutripoints'}</h2>
                <p class="loyalty-dashboard__section-subtitle">${window.strings?.loyalty?.logged_out?.heroSubtitle || 'Ofertas para si e para os seus patudos'}</p>
                <div class="loyalty-dashboard__info-grid">
                  <div class="loyalty-dashboard__info-card">
                    <svg class="loyalty-dashboard__info-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 2C7.89543 2 7 2.89543 7 4V5H6C4.34315 5 3 6.34315 3 8V19C3 20.6569 4.34315 22 6 22H18C19.6569 22 21 20.6569 21 19V8C21 6.34315 19.6569 5 18 5H17V4C17 2.89543 16.1046 2 15 2H9ZM9 4H15V6H9V4ZM6 7H18C18.5523 7 19 7.44772 19 8V19C19 19.5523 18.5523 20 18 20H6C5.44772 20 5 19.5523 5 19V8C5 7.44772 5.44772 7 6 7Z" fill="currentColor"/>
                    </svg>
                    <h3 class="loyalty-dashboard__info-title">Compre & Ganhe</h3>
                    <p class="loyalty-dashboard__info-text">${window.strings?.loyalty?.dashboard?.shopEarnText || 'Ganhe 1 Nutripoint por cada 1€ de compras. Cada compra aproxima-o do próximo nível e de mais produtos grátis.'}</p>
                  </div>
                  <div class="loyalty-dashboard__info-card">
                    <svg class="loyalty-dashboard__info-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor"/>
                    </svg>
                    <h3 class="loyalty-dashboard__info-title">${window.strings?.loyalty?.dashboard?.collectTitle || 'Acumule Pontos'}</h3>
                    <p class="loyalty-dashboard__info-text">${window.strings?.loyalty?.dashboard?.collectText || 'Os pontos são adicionados automaticamente após a sua encomenda'}</p>
                  </div>
                  <div class="loyalty-dashboard__info-card">
                    <svg class="loyalty-dashboard__info-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 7H16V5C16 3.34 14.66 2 13 2H11C9.34 2 8 3.34 8 5V7H4C2.9 7 2 7.9 2 9V20C2 21.1 2.9 22 4 22H20C21.1 22 22 21.1 22 20V9C22 7.9 21.1 7 20 7ZM10 5C10 4.45 10.45 4 11 4H13C13.55 4 14 4.45 14 5V7H10V5ZM12 15.5C10.62 15.5 9.5 14.38 9.5 13C9.5 11.62 10.62 10.5 12 10.5C13.38 10.5 14.5 11.62 14.5 13C14.5 14.38 13.38 15.5 12 15.5Z" fill="currentColor"/>
                    </svg>
                    <h3 class="loyalty-dashboard__info-title">${window.strings?.loyalty?.dashboard?.redeemTitle || 'Resgate Recompensas'}</h3>
                    <p class="loyalty-dashboard__info-text">${window.strings?.loyalty?.dashboard?.redeemText || 'Escolha recompensas e obtenha códigos promocionais instantaneamente'}</p>
                  </div>
                </div>
                <button type="button" class="loyalty-dashboard__login-button" onclick="document.querySelector('.gw-rd-earn-points-product-page-widget button, [class*=gw-rd] button, .gw-btn')?.click()">
                  ${window.strings?.loyalty?.dashboard?.loginButton || 'Entrar / Registar'}
                </button>
              </div>
            </div>
          </div>

          <!-- Offers Section with Tier Selector -->
          <div class="loyalty-offers-section">
            <h2 class="loyalty-offers-section__title">${window.strings?.loyalty?.dashboard?.offersTitle || 'As nossas ofertas para si'}</h2>

            <div class="loyalty-tier-selector">
              <button class="loyalty-tier-selector__button active" data-tier="tier_150">${window.strings?.loyalty?.dashboard?.tierButton1 || '1º Nível'}</button>
              <button class="loyalty-tier-selector__button" data-tier="tier_300">${window.strings?.loyalty?.dashboard?.tierButton2 || '2º Nível'}</button>
              <button class="loyalty-tier-selector__button" data-tier="tier_500">${window.strings?.loyalty?.dashboard?.tierButton3 || '3º Nível'}</button>
              <button class="loyalty-tier-selector__button" data-tier="tier_700">${window.strings?.loyalty?.dashboard?.tierButton4 || 'Nível Final'}</button>
            </div>

            <div class="loyalty-product-carousel">
              <div class="loyalty-product-carousel__loading">
                <div class="loyalty-dashboard__loader"></div>
              </div>
            </div>

            <a href="${loginUrl}" class="loyalty-dashboard__login-button">
              ${window.strings?.loyalty?.dashboard?.loginButton || 'Entrar / Registar'}
            </a>
          </div>

          <!-- FAQ Section -->
          <div class="loyalty-faq-section">
            <h2 class="loyalty-faq-section__title">${window.strings?.loyalty?.dashboard?.faqTitle || 'Perguntas Frequentes'}</h2>
            <div class="loyalty-faq-accordion" id="loyalty-faq-accordion">
              <!-- FAQ items will be loaded from section settings -->
            </div>
          </div>

          <!-- Icons with Text Section -->
          <section class="loyalty-icons-section">
            <div class="icons-with-text">
              <ul class="section-grid">
                <li class="icons-with-text__item">
                  <div class="icons-with-text__content">
                    <div class="icons-with-text__icon">
                      <svg class="icon icon--shield_checkmark" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 512 512">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M336 176L225.2 304 176 255.8" fill="none" stroke="currentColor" stroke-width="32"></path>
                        <path d="M463.1 112.37C373.68 96.33 336.71 84.45 256 48c-80.71 36.45-117.68 48.33-207.1 64.37C32.7 369.13 240.58 457.79 256 464c15.42-6.21 223.3-94.87 207.1-351.63z" stroke-linecap="round" stroke-linejoin="round" fill="none" stroke="currentColor" stroke-width="32"></path>
                      </svg>
                    </div>
                    <div class="icons-with-text__text-wrapper">
                      <div class="icons-with-text__heading"><strong>CONFIANÇA</strong></div>
                      <div class="icons-with-text__text">Mais de 10.000 clientes</div>
                    </div>
                  </div>
                </li>
                <li class="icons-with-text__item">
                  <div class="icons-with-text__content">
                    <div class="icons-with-text__icon">
                      <svg class="icon icon--truck" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 20 20">
                        <path d="M0 3.75156C0 3.47454 0.224196 3.24997 0.500755 3.24997H10.647C10.9235 3.24997 11.1477 3.47454 11.1477 3.75156V5.07505V5.63362V6.10938V13.6616C10.9427 14.0067 10.8813 14.1101 10.5516 14.6648L7.22339 14.6646V13.6614H10.1462V4.25316H1.00151V13.6614H2.6842V14.6646H0.500755C0.224196 14.6646 0 14.44 0 14.163V3.75156Z"></path>
                        <path d="M18.9985 8.08376L11.1477 6.10938V5.07505L19.6212 7.20603C19.8439 7.26203 20 7.46255 20 7.69253V14.1631C20 14.4401 19.7758 14.6647 19.4992 14.6647H17.3071V13.6615H18.9985V8.08376ZM11.1477 13.6616L13.3442 13.6615L13.3443 14.6647L10.5516 14.6648L11.1477 13.6616Z"></path>
                        <path d="M7.71269 14.1854C7.71269 15.6018 6.56643 16.75 5.15245 16.75C3.73847 16.75 2.59221 15.6018 2.59221 14.1854C2.59221 12.7691 3.73847 11.6209 5.15245 11.6209C6.56643 11.6209 7.71269 12.7691 7.71269 14.1854ZM5.15245 15.7468C6.01331 15.7468 6.71118 15.0478 6.71118 14.1854C6.71118 13.3231 6.01331 12.6241 5.15245 12.6241C4.29159 12.6241 3.59372 13.3231 3.59372 14.1854C3.59372 15.0478 4.29159 15.7468 5.15245 15.7468Z"></path>
                        <path d="M17.5196 14.1854C17.5196 15.6018 16.3733 16.75 14.9593 16.75C13.5454 16.75 12.3991 15.6018 12.3991 14.1854C12.3991 12.7691 13.5454 11.6209 14.9593 11.6209C16.3733 11.6209 17.5196 12.7691 17.5196 14.1854ZM14.9593 15.7468C15.8202 15.7468 16.5181 15.0478 16.5181 14.1854C16.5181 13.3231 15.8202 12.6241 14.9593 12.6241C14.0985 12.6241 13.4006 13.3231 13.4006 14.1854C13.4006 15.0478 14.0985 15.7468 14.9593 15.7468Z"></path>
                      </svg>
                    </div>
                    <div class="icons-with-text__text-wrapper">
                      <div class="icons-with-text__heading"><strong>ENTREGAS RÁPIDAS</strong></div>
                      <div class="icons-with-text__text">Entre 24h a 72h</div>
                    </div>
                  </div>
                </li>
                <li class="icons-with-text__item">
                  <div class="icons-with-text__content">
                    <div class="icons-with-text__icon">
                      <svg class="icon icon--chat_bubbles" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 512 512">
                        <path d="M431 320.6c-1-3.6 1.2-8.6 3.3-12.2a33.68 33.68 0 012.1-3.1A162 162 0 00464 215c.3-92.2-77.5-167-173.7-167-83.9 0-153.9 57.1-170.3 132.9a160.7 160.7 0 00-3.7 34.2c0 92.3 74.8 169.1 171 169.1 15.3 0 35.9-4.6 47.2-7.7s22.5-7.2 25.4-8.3a26.44 26.44 0 019.3-1.7 26 26 0 0110.1 2l56.7 20.1a13.52 13.52 0 003.9 1 8 8 0 008-8 12.85 12.85 0 00-.5-2.7z" stroke-linecap="round" stroke-miterlimit="10" fill="none" stroke="currentColor" stroke-width="32"></path>
                        <path d="M66.46 232a146.23 146.23 0 006.39 152.67c2.31 3.49 3.61 6.19 3.21 8s-11.93 61.87-11.93 61.87a8 8 0 002.71 7.68A8.17 8.17 0 0072 464a7.26 7.26 0 002.91-.6l56.21-22a15.7 15.7 0 0112 .2c18.94 7.38 39.88 12 60.83 12A159.21 159.21 0 00284 432.11" stroke-linecap="round" stroke-miterlimit="10" fill="none" stroke="currentColor" stroke-width="32"></path>
                      </svg>
                    </div>
                    <div class="icons-with-text__text-wrapper">
                      <div class="icons-with-text__heading"><strong>APOIO VETERINÁRIO</strong></div>
                      <div class="icons-with-text__text">Através do WhatsApp e E-mail</div>
                    </div>
                  </div>
                </li>
                <li class="icons-with-text__item">
                  <div class="icons-with-text__content">
                    <div class="icons-with-text__icon">
                      <svg class="icon icon--price_tags" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 512 512">
                        <path d="M403.29 32H280.36a14.46 14.46 0 00-10.2 4.2L24.4 281.9a28.85 28.85 0 000 40.7l117 117a28.86 28.86 0 0040.71 0L427.8 194a14.46 14.46 0 004.2-10.2v-123A28.66 28.66 0 00403.29 32z" stroke-linecap="round" stroke-linejoin="round" fill="none" stroke="currentColor" stroke-width="32"></path>
                        <path d="M352 144a32 32 0 1132-32 32 32 0 01-32 32z"></path>
                        <path d="M230 480l262-262a13.81 13.81 0 004-10V80" stroke-linecap="round" stroke-linejoin="round" fill="none" stroke="currentColor" stroke-width="32"></path>
                      </svg>
                    </div>
                    <div class="icons-with-text__text-wrapper">
                      <div class="icons-with-text__heading"><strong>MELHORES PREÇOS</strong></div>
                      <div class="icons-with-text__text">+ 4.000 Produtos</div>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </section>
        </div>
      `;

      // Initialize tier products carousel
      this.initTierCarousel();

      // Load FAQ from section settings
      this.loadFAQ();
    }

    async initTierCarousel() {
      if (!window.loyaltyAPI) {
        const strings = window.strings?.loyalty?.dashboard || {};
        this.renderError(
          strings.errorNotConfigured || "Loyalty system is not configured.",
        );
        return;
      }

      // If not available, try fetching from API
      const fetchUrl = "/tier-products";

      try {
        const response = await window.loyaltyAPI.request(fetchUrl);
        const data = response;

        if (data.success && data.tier_products) {
          this.tierProducts = data.tier_products;
          this.renderLoggedOutTierProducts("tier_150");
          this.attachTierSelectorListeners();
        } else {
          console.error(
            "[Loyalty Tier Products] Invalid response format:",
            data,
          );
          throw new Error("Invalid response format");
        }
      } catch (error) {
        console.error(
          "[Loyalty Tier Products] Failed to load tier products:",
          error,
        );

        // Try using mock data as ultimate fallback
        if (window.loyaltyAPI?.mockMode) {
          this.useMockTierProducts();
        } else {
          const carousel = this.querySelector(".loyalty-product-carousel");
          if (carousel) {
            carousel.innerHTML = `
              <div class="loyalty-product-carousel__error">
                <p>Unable to load products at this time.</p>
                <p class="loyalty-product-carousel__error-detail">${error.message}</p>
                <p class="loyalty-product-carousel__error-hint">Please log in to see available rewards.</p>
              </div>
            `;
          }
        }
      }
    }

    attachTierSelectorListeners() {
      const buttons = this.querySelectorAll(".loyalty-tier-selector__button");

      buttons.forEach((button) => {
        button.addEventListener("click", () => {
          // Update active state
          buttons.forEach((btn) => btn.classList.remove("active"));
          button.classList.add("active");

          // Render selected tier
          const tier = button.getAttribute("data-tier");
          this.renderLoggedOutTierProducts(tier);
        });
      });
    }

    useMockTierProducts() {
      // Use the mock data from loyaltyAPI if available
      if (window.loyaltyAPI) {
        this.tierProducts = window.loyaltyAPI.getMockTierProducts();
      } else {
        // Minimal fallback mock data
        this.tierProducts = {
          tier_150: [],
          tier_300: [],
          tier_500: [],
          tier_700: [],
        };
      }

      this.renderLoggedOutTierProducts("tier_150");
      this.attachTierSelectorListeners();
    }

    renderLoggedOutTierProducts(tier) {
      const carousel = this.querySelector(".loyalty-product-carousel");
      if (!carousel) return;

      // Tier points mapping
      const tierPointsMap = {
        'tier_150': 65,
        'tier_300': 140,
        'tier_500': 250,
        'tier_700': 400
      };

      const products = this.tierProducts?.[tier] || [];

      if (tier === "tier_700") {
        // Special rendering for vet consultation
        const tiersStrings = window.strings?.loyalty?.tiers || {};
        const vetTitle = tiersStrings.vetTierTitle || "Aconselhamento Veterinário Online";
        const vetDescription = tiersStrings.vetTierDescription || "Resgate 400 pontos para receber um aconselhamento nutricional personalizado ao seu animal de estimação.";
        const vetCta = tiersStrings.vetTierCta || "Saber mais";

        carousel.innerHTML = `
          <div class="loyalty-tier-vet">
            <div class="loyalty-tier-vet__content">
              <h3>${vetTitle}</h3>
              <p>${vetDescription}</p>
              <a href="${getLocalizedUrl('/pages/aconselhamento-veterinario')}" class="loyalty-tier-vet__cta">${vetCta}</a>
            </div>
            <div class="loyalty-tier-vet__image">
              <img src="${window.loyaltyConfig?.vetImageUrl || ''}" alt="" loading="lazy">
            </div>
          </div>
        `;
        return;
      }

      if (products.length === 0) {
        carousel.innerHTML =
          '<p class="loyalty-product-carousel__empty">No products available for this tier.</p>';
        return;
      }

      // Generate unique slider ID
      const sliderId = `LoggedOutSlider-${tier}`;

      // Get tier points from mapping (e.g., "tier_150" -> 65)
      const tierPoints = tierPointsMap[tier] || parseInt(tier.split('_')[1], 10);

      // Render product cards in carousel
      carousel.innerHTML = `
        <slider-component class="slider-mobile-gutter loyalty-tier-row__slider">
          <ul id="${sliderId}" class="grid product-grid contains-card contains-card--product contains-card--standard grid--4-col-desktop grid--2-col-tablet-down slider slider--desktop slider--tablet" role="list">
            ${products
              .map(
                (product, index) => `
              <li id="${sliderId}-Slide-${index + 1}" class="grid__item slider__slide">
                ${this.renderLoggedOutProductCard(product, tierPoints)}
              </li>
            `,
              )
              .join("")}
          </ul>
          ${
            products.length > 4
              ? `
            <div class="slider-buttons no-js-hidden">
              <button type="button" class="slider-button slider-button--prev" name="previous" aria-label="Slide left" aria-controls="${sliderId}">
                <svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path>
                </svg>
              </button>
              <div class="slider-counter caption">
                <span class="slider-counter--current">1</span>
                <span aria-hidden="true"> / </span>
                <span class="visually-hidden">of</span>
                <span class="slider-counter--total">${products.length}</span>
              </div>
              <button type="button" class="slider-button slider-button--next" name="next" aria-label="Slide right" aria-controls="${sliderId}">
                <svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path>
                </svg>
              </button>
            </div>
          `
              : ""
          }
        </slider-component>
      `;
    }

    renderLoggedOutProductCard(product, tierPoints) {
      const strings = window.strings?.loyalty?.tiers || {};
      const firstVariant = product.variants?.[0] || {};

      return `
        <div class="card-wrapper product-card-wrapper underline-links-hover">
          <div class="card card--standard card--media">
            <div class="card__inner color-scheme-1 gradient ratio" style="--ratio-percent: 100%;">
              <div class="card__media">
                <div class="media media--transparent media--hover-effect">
                  ${
                    product.featured_image
                      ? `
                    <img
                      srcset="${product.featured_image}"
                      src="${product.featured_image}"
                      sizes="(min-width: 990px) 25vw, (min-width: 750px) 33vw, 50vw"
                      alt="${product.title}"
                      class="motion-reduce"
                      loading="lazy"
                      width="300"
                      height="300"
                    >
                  `
                      : `
                    <div class="card__media-placeholder">
                      <svg class="placeholder-svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525.5 525.5">
                        <path d="M324.5 212.7H203c-1.6 0-2.8 1.3-2.8 2.8V308c0 1.6 1.3 2.8 2.8 2.8h121.6c1.6 0 2.8-1.3 2.8-2.8v-92.5c0-1.6-1.3-2.8-2.9-2.8zm1.1 95.3c0 .6-.5 1.1-1.1 1.1H203c-.6 0-1.1-.5-1.1-1.1v-92.5c0-.6.5-1.1 1.1-1.1h121.6c.6 0 1.1.5 1.1 1.1V308z"/>
                        <path d="M210.4 299.5H240v.1s.1 0 .2-.1h75.2v-76.2h-105v76.2zm1.8-7.2l20-20c1.6-1.6 3.8-2.5 6.1-2.5s4.5.9 6.1 2.5l1.5 1.5 16.8 16.8c-12.9 3.3-20.7 6.3-22.8 7.2h-27.7v-5.5zm101.5-10.1c-20.1 1.7-36.7 4.8-49.1 7.9l-16.9-16.9 26.3-26.3c1.6-1.6 3.8-2.5 6.1-2.5s4.5.9 6.1 2.5l27.5 27.5v7.8zm-68.9 15.5c9.7-3.5 24.9-6.9 44.4-9.5l11.4 11.4H244.8v-.1c.1 0 .1.1 0 .1l-.1-.1.1.1zm-2.5-18.8c-.9-.9-1.9-1.8-2.9-2.6l20.7-20.7c.9.9 1.9 1.8 2.8 2.7l-20.6 20.6zm-18.8-7.2l-9.9-9.9-1.5-1.5c-.4-.4-.9-.7-1.3-1.1v-54.3h95.6v54.3c-.4.4-.9.8-1.3 1.1l-9.9 9.9-26.3-26.3c-3.6-3.6-9.4-3.6-13 0l-32.4 32.4c0-.1 0-.1 0 0z"/>
                      </svg>
                    </div>
                  `
                  }
                </div>
              </div>
              <div class="card__content card__content--badge">
                <div class="card__badge bottom left">
                  <span class="badge badge--bottom-left loyalty-product-badge">${strings.free_badge || "GRÁTIS"}</span>
                </div>
              </div>
            </div>
            <div class="card__content">
              <div class="card__information">
                <h3 class="card__heading" style="font-size: 1.125rem;">
                  <a href="${product.url}" class="full-unstyled-link">
                    ${product.title}
                  </a>
                </h3>
                <div class="card-information">
                  <span class="caption-large light"></span>
                  <div class="price">
                    <div class="price__container">
                      <div class="price__sale">
                        <span class="visually-hidden visually-hidden--inline">${strings.salePrice || "Sale price"}</span>
                        <span class="price-item price-item--sale price-item--last">
                          €0.00
                        </span>
                        <span class="visually-hidden visually-hidden--inline">${strings.originalPrice || "Original price"}</span>
                        <s class="price-item price-item--regular">
                          ${this.formatPrice(firstVariant.price)}
                        </s>
                      </div>
                    </div>
                  </div>
                  <div class="card-loyalty-info" style="margin-top: 0.75rem;">
                    <span class="card-loyalty-points" style="color: #FF6B00; font-weight: 700; font-size: 1.25rem; display: block;">
                      ${tierPoints} ${strings.nutripoints || "NutriPoints"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div class="card__content">
              <div class="card__information">
                <div class="loyalty-tier__product-actions">
                  <a href="${product.url}" class="button button--full-width">
                    ${window.strings?.loyalty?.logged_out?.viewProduct || 'Receber Oferta'}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      `;
    }

    loadFAQ() {
      const faqContainer = this.querySelector("#loyalty-faq-accordion");
      if (!faqContainer) return;

      // FAQ items from translations (multilingual support)
      const faq = window.strings?.loyalty?.faq || {};
      const faqItems = [
        { question: faq.question1, answer: faq.answer1 },
        { question: faq.question2, answer: faq.answer2 },
        { question: faq.question3, answer: faq.answer3 },
        { question: faq.question4, answer: faq.answer4 },
        { question: faq.question5, answer: faq.answer5 },
        { question: faq.question6, answer: faq.answer6 },
      ].filter(item => item.question && item.answer);

      const faqHTML = faqItems
        .map(
          (item, index) => `
        <details class="loyalty-faq-item">
          <summary class="loyalty-faq-item__question">${item.question}</summary>
          <div class="loyalty-faq-item__answer">
            <p>${item.answer}</p>
          </div>
        </details>
      `,
        )
        .join("");

      faqContainer.innerHTML = faqHTML;
    }

    renderError(message) {
      const strings = window.strings?.loyalty?.dashboard || {};

      this.innerHTML = `
        <div class="loyalty-dashboard loyalty-dashboard--error">
          <p class="loyalty-dashboard__error">⚠️ ${message}</p>
          <button class="loyalty-dashboard__retry" onclick="this.closest('loyalty-dashboard').loadData()">
            ${strings.retry || "Retry"}
          </button>
        </div>
      `;
    }
  }

  customElements.define("loyalty-dashboard", LoyaltyDashboard);
}

/**
 * Global Loyalty Balance Manager
 * Fetches and stores customer loyalty balance globally for use across the site
 */
(function initGlobalLoyaltyBalance() {
  // Initialize global loyalty balance object
  window.loyaltyBalance = {
    points: 0,
    loaded: false,
    loading: false
  };

  // Only fetch if loyalty is enabled and customer is logged in
  if (!window.loyaltyConfig?.enabled || !window.loyaltyConfig?.customerId) {
    return;
  }

  // Wait for loyalty API to be available
  function fetchBalance() {
    if (!window.loyaltyAPI) {
      // Retry after a short delay if API isn't ready yet
      setTimeout(fetchBalance, 100);
      return;
    }

    if (window.loyaltyBalance.loading) {
      return; // Already loading
    }

    window.loyaltyBalance.loading = true;

    window.loyaltyAPI.getBalance()
      .then(data => {
        window.loyaltyBalance.points = data.balance || 0;
        window.loyaltyBalance.loaded = true;
        window.loyaltyBalance.loading = false;

        // Dispatch custom event for components that need to react to balance updates
        window.dispatchEvent(new CustomEvent('loyaltyBalanceLoaded', {
          detail: { points: window.loyaltyBalance.points }
        }));
      })
      .catch(error => {
        console.error('[Loyalty] Failed to fetch global balance:', error);
        window.loyaltyBalance.loaded = true;
        window.loyaltyBalance.loading = false;
      });
  }

  // Start fetching balance
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fetchBalance);
  } else {
    fetchBalance();
  }
})();

// Helper function to decode HTML entities (reused from above)
function decodeHTML(html) {
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  return txt.value;
}

// Initialize product info badges on page load
(function() {
  function initProductInfoBadges() {
    // Decode HTML for loyalty points text in product info badges
    const loyaltyTextElements = document.querySelectorAll('.product-info-badges__text-content[data-raw-text]');
    loyaltyTextElements.forEach(element => {
      const rawText = element.getAttribute('data-raw-text');
      if (rawText) {
        element.innerHTML = decodeHTML(rawText);
      }
    });

    // Handle customer balance check for offer badges
    const offerBadges = document.querySelectorAll('.product-info-badges__item--offer[data-customer-balance-check="true"]');
    if (offerBadges.length > 0) {
      // Wait for balance to load
      const checkBalance = () => {
        // Wait for loyaltyBalance to be initialized
        if (!window.loyaltyBalance) {
          setTimeout(checkBalance, 50);
          return;
        }

        if (window.loyaltyBalance.loaded) {
          const customerBalance = window.loyaltyBalance.points || 0;

          offerBadges.forEach(badge => {
            const productPoints = parseInt(badge.getAttribute('data-product-points') || 0);
            if (customerBalance + productPoints >= 65) {
              badge.style.display = '';
            }
          });
        } else if (!window.loyaltyBalance.loading) {
          // Balance not loaded yet, try again
          setTimeout(checkBalance, 100);
        }
      };
      checkBalance();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductInfoBadges);
  } else {
    initProductInfoBadges();
  }
})();
